import type React from 'react';

/**
 * Boutons-pilules de la charte (passation §3) : cinq variantes, rayon 999, hauteurs 36 / 34 / 32.
 *
 * `ui/Button.tsx` est l'ANCIEN bouton (rayon 6, hauteurs 28/34/40) et reste en place pour les 61
 * fichiers qui l'utilisent ; les modules refaits écrivent leurs pilules avec ces classes, au lieu de
 * recopier la même chaîne Tailwind dans chaque fichier (Planning, Prestations et Événementiel l'ont
 * fait chacun de leur côté — ce fichier est la version commune).
 *
 * La pilule principale est `bg-content text-surface`, jamais `bg-ink text-white` : `--color-ink`
 * vaut #15161a dans les deux thèmes, le bouton disparaîtrait sur fond sombre.
 * L'accent (`--acc`) se pose en `style`, pas en classe : `style={PILL_ACCENT_STYLE}`.
 */
const BASE = 'inline-flex items-center justify-center gap-[7px] rounded-full font-semibold whitespace-nowrap shrink-0 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

const H = { 36: 'h-9 px-4 text-[12.5px]', 34: 'h-[34px] px-[13px] text-[12.5px]', 32: 'h-8 px-[13px] text-[12px]' } as const;
type Hauteur = keyof typeof H;

/** Principal — l'action de l'écran, une seule par pied de modale. */
export const pillPrimary = (h: Hauteur = 36) => `${BASE} ${H[h]} bg-content text-surface hover:opacity-90`;
/** Secondaire — Annuler, Modifier, actions de second rang. */
export const pillSecondary = (h: Hauteur = 36) => `${BASE} ${H[h]} border border-border bg-surface text-content hover:border-content`;
/** Discret — actions tertiaires. */
export const pillGhost = (h: Hauteur = 36) => `${BASE} ${H[h]} text-content-muted hover:text-content hover:bg-surface-soft`;
/** Accent de l'ambiance — l'action colorée d'un en-tête (à coupler avec PILL_ACCENT_STYLE). */
export const pillAccent = (h: Hauteur = 36) => `${BASE} ${H[h]} text-brand-fg hover:opacity-90`;
export const PILL_ACCENT_STYLE: React.CSSProperties = { background: 'var(--acc)' };
/** Destructif discret — suppression dans un pied de fiche : texte rouge doux, fond au survol. */
export const pillDangerGhost = (h: Hauteur = 36) => `${BASE} ${H[h]} text-state-danger hover:bg-state-danger-soft`;
/** Destructif plein — suppression définitive uniquement (à coupler avec PILL_DESTRUCTIVE_STYLE). */
export const pillDestructive = (h: Hauteur = 36) => `${BASE} ${H[h]} text-white hover:opacity-90`;
export const PILL_DESTRUCTIVE_STYLE: React.CSSProperties = { background: 'var(--color-destructive)' };
/** Pilule « attention » posée sur un bandeau d'alerte : bordure et texte du jeton warn. */
export const pillWarn = (h: Hauteur = 32) => `${BASE} ${H[h]} border border-state-warn/30 bg-surface text-state-warn hover:border-state-warn`;

/** Sur-titre de section (10 px, capitales, .09em) — le même partout. */
export const SURTITRE = 'text-[10px] font-bold uppercase tracking-[.09em] text-content-muted';
